import { ChevronDown } from 'lucide-react';
import { useState, type CSSProperties, type FC } from 'react';

import type { StorefrontMenuItem } from '@/features/storefront/api/storefront';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import {
  HeaderBannerRail,
  HeaderCategoryLinks,
  MenuLink,
  MobileNav,
  StoreLogo,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import { FittingNav } from '@/features/storefront/layouts/chrome/FittingNav';
import { menuItems, type HeaderProps } from '@/features/storefront/layouts/chrome/types';
import { cn } from '@/lib/cn';

/**
 * The rail's ground, and the one thing in this file worth explaining.
 *
 * The shared announcement rail paints itself in the theme's text colour and
 * sets its copy in the theme's background colour, so it inverts correctly on
 * any palette a merchant lands on. This theme wants that inversion in sand
 * rather than in white - the rail and the wordmark are the two places the
 * accent lives - so rather than fork the rail, the deck redefines the variable
 * the rail reads: inside the band the page colour *is* the sand. Nothing
 * outside the band is touched, and a merchant who repaints their palette still
 * gets a rail that inverts.
 */
const RAIL_STYLE = {
  '--storefront-background': 'var(--storefront-accent)',
} as CSSProperties;

/** The masthead's navigation voice, worn by the menu and by its stand-in alike. */
const FEAST_NAV = 'items-center gap-7 whitespace-nowrap text-[15px]';

/**
 * One item on the bar, and whatever the merchant filed under it.
 *
 * A homeware shop is navigated by department and then by shelf - somebody
 * wants a wok, not "cookware" - so an item with children opens them rather
 * than making a shopper land on a department page first. The item itself stays
 * a link either way: the shelves are a shortcut, never the only way through.
 *
 * Opens on hover for a pointer and on focus for a keyboard, and closes on
 * Escape. No click handler on the trigger, because the trigger is the link.
 */
function NavItem({ item, preview }: { item: StorefrontMenuItem; preview?: boolean }) {
  const [open, setOpen] = useState(false);
  const children = item.items ?? [];

  if (children.length === 0) {
    return <MenuLink item={item} preview={preview} className="hover:opacity-60" />;
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <span className="flex items-center gap-1.5">
        <MenuLink item={item} preview={preview} className="hover:opacity-60" />
        <ChevronDown
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </span>

      {open ? (
        <div
          className="absolute left-1/2 top-full z-40 min-w-56 -translate-x-1/2 border py-2 shadow-xl"
          style={{
            backgroundColor: 'var(--storefront-background)',
            borderColor: 'var(--storefront-surface)',
          }}
        >
          {children.map((child) => (
            <MenuLink
              key={`${child.label}:${child.url}`}
              item={child}
              preview={preview}
              className="block px-5 py-2 text-[15px] hover:opacity-60"
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A homeware shop's masthead, in two decks.
 *
 * The sand rail carries the standing promises - free delivery, the returns
 * window, the day new things land - which is what a shop selling a ₹900 plate
 * beside a ₹65,000 espresso machine has to say before anything else. The deck
 * under it is the shop itself: the wordmark on the left, the departments
 * across the middle, and the shopper's own corner on the right.
 *
 * The wordmark is set in capitals with a great deal of tracking, which is the
 * whole of this theme's logotype and the only place on the page where letters
 * are spaced apart on purpose.
 *
 * Like Serra and Voltic, the bar stands the shop's own shelves in when the
 * merchant has built no menu - a homeware header with nothing in it is not a
 * quieter header, it is a shop with no way to the plates.
 */
export const Header: FC<HeaderProps> = ({
  storeName,
  logoUrl,
  description,
  header,
  menu,
  endSlot,
  preview,
}) => {
  const items = menuItems(menu);

  return (
    <header
      className={cn('border-b', header.sticky && !preview && 'sticky top-0 z-40')}
      style={{
        borderColor: 'var(--storefront-surface)',
        backgroundColor: 'var(--storefront-background)',
      }}
    >
      {/* This family is drawn around a rail, so with no banners written it
          keeps carrying the shop's own line rather than closing up. */}
      <div style={RAIL_STYLE}>
        <HeaderBannerRail header={header} fallback={description} preview={preview} />
      </div>

      <StorefrontContainer className="flex min-h-[4.5rem] items-center gap-6 py-3">
        <MobileNav menu={menu} hiddenFrom="lg:hidden" preview={preview} categoryTree />
        <StoreLogo
          storeName={storeName}
          logoUrl={logoUrl}
          logoWidthPx={header.logo_width_px}
          showStoreName={header.show_store_name}
          preview={preview}
          className="gap-3"
          nameClassName="text-2xl uppercase tracking-[0.2em] sm:text-[1.6rem]"
          nameStyle={{
            fontFamily: 'var(--storefront-heading-font)',
            fontWeight: 'var(--storefront-heading-weight)' as CSSProperties['fontWeight'],
          }}
        />

        {/* The bar takes the middle of the deck from the large breakpoint up
            and stands down below it, where the wordmark and the corner have the
            row to themselves and the sheet carries the navigation. */}
        <div className="hidden min-w-0 flex-1 justify-center lg:flex">
          {items.length > 0 ? (
            /* As many departments as fit, the rest under "More": a merchant's
               "Nutrition & Supplements" is wider than "Decor", and a bar that
               cannot wrap would spill over the wordmark and the corner. */
            <FittingNav
              items={items}
              label={menu?.name}
              preview={preview}
              navClassName={FEAST_NAV}
              chevronGapClassName="gap-1.5"
              moreClassName="hover:opacity-60"
              renderItem={(item) => <NavItem key={`${item.label}:${item.url}`} item={item} preview={preview} />}
            />
          ) : preview ? null : (
            /* No menu built yet. Six, because this bar has a wide page column
               to spend and the shelves are the way into the catalogue.
               Skipped in preview, where no storefront host is resolved and the
               query would ask about the wrong shop. */
            <HeaderCategoryLinks
              className={cn('flex', FEAST_NAV)}
              linkClassName="hover:opacity-60"
              header={header}
              limit={6}
            />
          )}
        </div>

        <div className="ms-auto flex shrink-0 items-center gap-5">{endSlot}</div>
      </StorefrontContainer>
    </header>
  );
};
